(function () {
  var STORAGE_KEY = 'theme:wishlist';

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {}
  }

  function emit(items) {
    document.dispatchEvent(
      new CustomEvent('wishlist:change', { detail: { items: items, count: items.length } })
    );
  }

  var Wishlist = {
    get: function () {
      return read();
    },
    has: function (id) {
      if (id == null) return false;
      var sid = String(id);
      return read().some(function (item) {
        return String(item.id) === sid;
      });
    },
    add: function (id, handle) {
      if (id == null || !handle) return;
      var sid = String(id);
      var items = read();
      if (items.some(function (item) { return String(item.id) === sid; })) return;
      items.push({ id: id, handle: handle, addedAt: Date.now() });
      write(items);
      emit(items);
    },
    remove: function (id) {
      var sid = String(id);
      var next = read().filter(function (item) {
        return String(item.id) !== sid;
      });
      write(next);
      emit(next);
    },
    toggle: function (id, handle) {
      if (this.has(id)) this.remove(id);
      else this.add(id, handle);
    },
    count: function () {
      return read().length;
    },
    clear: function () {
      write([]);
      emit([]);
    }
  };

  window.Wishlist = Wishlist;

  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    var items;
    try {
      items = JSON.parse(event.newValue) || [];
    } catch (err) {
      items = [];
    }
    if (!Array.isArray(items)) items = [];
    emit(items);
  });

  var WishlistButton = class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._productId = this.getAttribute('product-id');
      this._productHandle = this.getAttribute('product-handle');
      this._sync();
      this.addEventListener('click', this._onClick.bind(this), {
        signal: this._abortController.signal
      });
      document.addEventListener('wishlist:change', this._sync.bind(this), {
        signal: this._abortController.signal
      });
    }
    disconnectedCallback() {
      if (this._abortController) this._abortController.abort();
    }
    _sync() {
      var active = Wishlist.has(this._productId);
      this.setAttribute('aria-pressed', active ? 'true' : 'false');
      var onLabel = this.getAttribute('aria-label-on');
      var offLabel = this.getAttribute('aria-label-off');
      if (onLabel && offLabel) {
        this.setAttribute('aria-label', active ? onLabel : offLabel);
      }
    }
    _onClick(event) {
      if (event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      Wishlist.toggle(this._productId, this._productHandle);
    }
  };

  if (!window.customElements.get('wishlist-button')) {
    window.customElements.define('wishlist-button', WishlistButton);
  }

  var WishlistCount = class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._bubble = this.closest('.count-bubble');
      this._sync();
      document.addEventListener('wishlist:change', this._sync.bind(this), {
        signal: this._abortController.signal
      });
      window.addEventListener('pageshow', this._sync.bind(this), {
        signal: this._abortController.signal
      });
    }
    disconnectedCallback() {
      if (this._abortController) this._abortController.abort();
    }
    _sync() {
      var count = Wishlist.count();
      this.textContent = count;
      var target = this._bubble || this;
      target.classList.toggle('opacity-0', count === 0);
    }
  };

  if (!window.customElements.get('wishlist-count')) {
    window.customElements.define('wishlist-count', WishlistCount);
  }

  var WishlistPage = class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._grid = this.querySelector('[data-wishlist-grid]');
      this._empty = this.querySelector('[data-wishlist-empty]');
      this._actions = this.querySelector('[data-wishlist-actions]');
      this._addAllBtn = this.querySelector('[data-wishlist-add-all]');
      this._clearBtn = this.querySelector('[data-wishlist-clear]');
      this._status = this.querySelector('[data-wishlist-status]');
      this._removeLabel = this.getAttribute('data-label-remove') || 'Remove from wishlist';
      this._soldOutLabel = this.getAttribute('data-label-sold-out') || 'Sold out';
      this._confirmClearMsg = this.getAttribute('data-confirm-clear') || 'Remove all products from wishlist?';
      this._products = [];

      this._render();

      document.addEventListener('wishlist:change', this._render.bind(this), {
        signal: this._abortController.signal
      });
      if (this._addAllBtn) {
        this._addAllBtn.addEventListener('click', this._onAddAll.bind(this), {
          signal: this._abortController.signal
        });
      }
      if (this._clearBtn) {
        this._clearBtn.addEventListener('click', this._onClear.bind(this), {
          signal: this._abortController.signal
        });
      }
    }
    disconnectedCallback() {
      if (this._abortController) this._abortController.abort();
    }
    async _render() {
      var items = Wishlist.get().slice().sort(function (a, b) {
        return (b.addedAt || 0) - (a.addedAt || 0);
      });

      if (items.length === 0) {
        this._products = [];
        if (this._grid) this._grid.innerHTML = '';
        this._toggleEmpty(true);
        return;
      }

      var results = await Promise.all(
        items.map(function (item) {
          return fetch('/products/' + encodeURIComponent(item.handle) + '.js')
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        })
      );

      var valid = [];
      for (var i = 0; i < items.length; i++) {
        if (results[i]) valid.push(results[i]);
        else Wishlist.remove(items[i].id);
      }

      this._products = valid;
      this._toggleEmpty(valid.length === 0);

      if (this._grid && valid.length > 0) {
        this._grid.innerHTML = valid.map(this._renderCard.bind(this)).join('');
      }
    }
    _renderCard(product) {
      var image = product.featured_image
        || (product.media && product.media[0] && product.media[0].src)
        || '';
      var title = this._escape(product.title);
      var handle = this._escape(product.handle);
      var url = '/products/' + handle;
      var price = this._formatPrice(product.price);
      var comparePrice = product.compare_at_price && product.compare_at_price > product.price
        ? this._formatPrice(product.compare_at_price)
        : '';
      var removeLabel = this._escape(this._removeLabel);

      return (
        '<article class="wishlist-card">'
          + '<div class="wishlist-card__media-wrap">'
            + '<a href="' + url + '" class="wishlist-card__media" aria-label="' + title + '">'
              + (image ? '<img src="' + image + '" alt="' + title + '" loading="lazy">' : '')
            + '</a>'
            + '<wishlist-button'
              + ' product-id="' + product.id + '"'
              + ' product-handle="' + handle + '"'
              + ' class="wishlist-card__remove"'
              + ' aria-label="' + removeLabel + '"'
              + ' aria-label-on="' + removeLabel + '"'
              + ' aria-label-off="' + removeLabel + '"'
            + '>'
              + '<svg role="presentation" focusable="false" width="14" height="14" viewBox="0 0 14 14">'
                + '<path d="M3 3l8 8M11 3L3 11" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" fill="none"/>'
              + '</svg>'
            + '</wishlist-button>'
          + '</div>'
          + '<div class="wishlist-card__info">'
            + '<a href="' + url + '" class="wishlist-card__title bold link-faded-reverse">' + title + '</a>'
            + '<div class="wishlist-card__price-row">'
              + '<span class="wishlist-card__price">' + price + '</span>'
              + (comparePrice ? '<s class="wishlist-card__price-compare text-subdued">' + comparePrice + '</s>' : '')
              + (product.available ? '' : '<span class="wishlist-card__sold-out">' + this._soldOutLabel + '</span>')
            + '</div>'
          + '</div>'
        + '</article>'
      );
    }
    _formatPrice(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      }
      var amount = (cents / 100).toFixed(2).replace('.', ',');
      var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
      return amount + (cur ? ' ' + cur : '');
    }
    _escape(value) {
      return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
      });
    }
    _toggleEmpty(isEmpty) {
      if (this._empty) this._empty.hidden = !isEmpty;
      if (this._grid) this._grid.hidden = isEmpty;
      if (this._actions) this._actions.hidden = isEmpty;
    }
    async _onAddAll() {
      if (!this._products || this._products.length === 0) return;

      var items = [];
      var skipped = 0;
      for (var i = 0; i < this._products.length; i++) {
        var product = this._products[i];
        var variant = (product.variants || []).find(function (v) { return v.available; });
        if (variant) items.push({ id: variant.id, quantity: 1 });
        else skipped++;
      }

      if (items.length === 0) {
        this._setStatus(this.getAttribute('data-msg-none-available') || 'No available products to add.');
        return;
      }

      this._addAllBtn.disabled = true;
      this._setStatus('');

      try {
        var addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
        if (!addRes.ok) throw new Error('cart add failed');

        var cart = await fetch('/cart.js').then(function (r) { return r.json(); });
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        }

        if (skipped > 0) {
          var tmpl = this.getAttribute('data-msg-added-with-skip') || 'Added to cart. %{skipped} product(s) were unavailable.';
          this._setStatus(tmpl.replace('%{skipped}', skipped));
        } else {
          this._setStatus(this.getAttribute('data-msg-added') || 'Added to cart.');
        }
      } catch (e) {
        this._setStatus(this.getAttribute('data-msg-error') || 'Something went wrong. Try again.');
      } finally {
        this._addAllBtn.disabled = false;
      }
    }
    _onClear() {
      if (!window.confirm(this._confirmClearMsg)) return;
      Wishlist.clear();
    }
    _setStatus(text) {
      if (this._status) this._status.textContent = text || '';
    }
  };

  if (!window.customElements.get('wishlist-page')) {
    window.customElements.define('wishlist-page', WishlistPage);
  }
})();
